/**
 * StrategySummary — Round 218A Phase H (2026-02-07).
 *
 * The strategist's post-onboarding brief. Rendered inside the WOW
 * moment step during onboarding, and again inside the Dashboard's
 * Strategy Summary modal.
 *
 * Reads /api/onboarding/wow-summary. Consumes ZERO Business Credits.
 */
import React, { useEffect, useState } from 'react';
import { onboardingAPI } from '@/utils/api';
import {
  CheckCircle, Target, Warning, Sparkle, ArrowRight, TrendUp,
  Package, RocketLaunch, Compass,
} from '@phosphor-icons/react';

const ASSET_META = {
  sales_page:   { label: 'Sales Page',   icon: RocketLaunch },
  lead_magnet:  { label: 'Lead Magnet',  icon: Sparkle },
  content_pack: { label: 'Content Pack', icon: Compass },
  offer_sheet:  { label: 'Offer Sheet',  icon: Package },
  brand_kit:    { label: 'Brand Kit',    icon: Sparkle },
  dfy_video:    { label: 'DFY Video',    icon: TrendUp },
};


const StrategySummary = ({
  summary: initialSummary,
  autoFetch = true,
  showChecklist = true,
  onBuildFirstAsset = null,
}) => {
  const [summary, setSummary] = useState(initialSummary || null);
  const [loading, setLoading] = useState(!initialSummary && autoFetch);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialSummary) {
      setSummary(initialSummary);
      return;
    }
    if (!autoFetch) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await onboardingAPI.wowSummary();
        if (cancelled) return;
        setSummary(res?.summary || null);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialSummary, autoFetch]);

  if (loading) {
    return (
      <div data-testid="strategy-summary-loading" className="p-4 text-slate-400 text-sm">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-3/4 bg-slate-700/40 rounded" />
          <div className="h-3 w-full bg-slate-700/30 rounded" />
          <div className="h-3 w-5/6 bg-slate-700/30 rounded" />
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div data-testid="strategy-summary-error" className="p-4 text-amber-300 text-sm">
        Your strategist is still finishing your brief — check back in a moment.
      </div>
    );
  }

  const opp = summary.biggest_opportunity || {};
  const risk = summary.biggest_risk || {};
  const first = summary.recommended_first_asset || {};
  const AssetIcon = (ASSET_META[first.asset_kind] || {}).icon || Package;
  const assetLabel = (ASSET_META[first.asset_kind] || {}).label || 'First Asset';

  return (
    <div data-testid="strategy-summary" className="space-y-4">
      {/* Hero */}
      {summary.hero_headline && (
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.05] p-4" data-testid="strategy-summary-hero">
          <div className="flex items-center gap-2 text-emerald-300 text-[11px] font-semibold uppercase tracking-wider">
            <Sparkle size={14} weight="fill" /> Your strategist&rsquo;s brief
          </div>
          <h3 className="mt-1 text-lg sm:text-xl font-bold text-white leading-tight" data-testid="strategy-summary-headline">
            {summary.hero_headline}
          </h3>
          {summary.hero_subline && (
            <p className="mt-1 text-sm text-slate-300" data-testid="strategy-summary-subline">
              {summary.hero_subline}
            </p>
          )}
        </div>
      )}

      {/* Positioning line */}
      {summary.positioning_line && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3" data-testid="strategy-summary-positioning">
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Positioning</div>
          <p className="text-sm text-slate-100 mt-1">{summary.positioning_line}</p>
        </div>
      )}

      {/* Opportunity + Risk row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {opp.title && (
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] p-3" data-testid="strategy-summary-opportunity">
            <div className="flex items-center gap-2 text-emerald-300 text-[11px] font-semibold uppercase tracking-wider">
              <Target size={14} weight="fill" /> Biggest opportunity
            </div>
            <h4 className="mt-1 text-sm font-semibold text-white leading-snug">{opp.title}</h4>
            {opp.why && <p className="mt-1 text-xs text-slate-300">{opp.why}</p>}
            {opp.expected_impact && (
              <p className="mt-2 text-[11px] text-emerald-200/90"><span className="font-semibold">Impact:</span> {opp.expected_impact}</p>
            )}
          </div>
        )}
        {risk.title && (
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-3" data-testid="strategy-summary-risk">
            <div className="flex items-center gap-2 text-amber-300 text-[11px] font-semibold uppercase tracking-wider">
              <Warning size={14} weight="fill" /> Biggest risk
            </div>
            <h4 className="mt-1 text-sm font-semibold text-white leading-snug">{risk.title}</h4>
            {risk.why && <p className="mt-1 text-xs text-slate-300">{risk.why}</p>}
            {risk.mitigation && (
              <p className="mt-2 text-[11px] text-amber-200/90"><span className="font-semibold">Do this:</span> {risk.mitigation}</p>
            )}
          </div>
        )}
      </div>

      {/* Recommended first asset */}
      {first.title && (
        <div className="rounded-xl border border-blue-500/25 bg-blue-500/[0.05] p-3" data-testid="strategy-summary-first-asset">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-blue-500/20 p-2 flex-shrink-0">
              <AssetIcon size={20} weight="fill" className="text-blue-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-blue-300 text-[11px] font-semibold uppercase tracking-wider">
                Recommended first asset · {assetLabel}
              </div>
              <h4 className="mt-1 text-sm font-semibold text-white leading-snug">{first.title}</h4>
              {first.why && <p className="mt-1 text-xs text-slate-300">{first.why}</p>}
              {first.one_line_brief && (
                <p className="mt-2 text-[11px] text-slate-400 italic">{first.one_line_brief}</p>
              )}
            </div>
          </div>
          {typeof onBuildFirstAsset === 'function' && (
            <button
              onClick={() => onBuildFirstAsset(first)}
              data-testid="strategy-summary-build-first-asset"
              className="mt-3 w-full sm:w-auto px-4 py-2 bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              Build this next <ArrowRight size={14} weight="bold" />
            </button>
          )}
        </div>
      )}

      {/* Next three moves */}
      {Array.isArray(summary.next_three_moves) && summary.next_three_moves.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3" data-testid="strategy-summary-next-moves">
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Your next three moves</div>
          <ol className="space-y-2">
            {summary.next_three_moves.map((m, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" data-testid={`strategy-summary-move-${i + 1}`}>
                <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-300 text-[11px] font-bold flex items-center justify-center">
                  {m.order || i + 1}
                </span>
                <div className="min-w-0">
                  <div className="text-slate-100 font-medium">{m.action}</div>
                  {m.why && <div className="text-xs text-slate-400 mt-0.5">{m.why}</div>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Strengths + weaknesses */}
      {(summary.strengths?.length > 0 || summary.weaknesses?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {summary.strengths?.length > 0 && (
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3" data-testid="strategy-summary-strengths">
              <div className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-1">Strengths</div>
              <ul className="text-xs text-slate-300 space-y-1">
                {summary.strengths.map((s, i) => <li key={i}>· {s}</li>)}
              </ul>
            </div>
          )}
          {summary.weaknesses?.length > 0 && (
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3" data-testid="strategy-summary-weaknesses">
              <div className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider mb-1">Weaknesses to address</div>
              <ul className="text-xs text-slate-300 space-y-1">
                {summary.weaknesses.map((w, i) => <li key={i}>· {w}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Intelligence sections checklist */}
      {showChecklist && Array.isArray(summary.intelligence_sections) && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3" data-testid="strategy-summary-sections">
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Intelligence prepared for you</div>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {summary.intelligence_sections.map((s) => (
              <li
                key={s.key}
                data-testid={`strategy-summary-section-${s.key}`}
                className={`flex items-center gap-1.5 text-xs ${s.ready ? 'text-emerald-300' : 'text-amber-300'}`}
              >
                <CheckCircle size={13} weight={s.ready ? 'fill' : 'regular'} />
                {s.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default StrategySummary;
